import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import type { Journey } from '@hellenica/shared';

const journeySchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z.string().optional(),
  subtitle: z.string().optional(),
  description: z.string().optional(),
  duration: z.string().optional(),
  price_from: z.number().optional(),
  price_display: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']),
  featured: z.boolean(),
  meta_title: z.string().optional(),
  meta_description: z.string().optional(),
});

type JourneyFormData = z.infer<typeof journeySchema>;

export default function JourneyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id;

  const { data: journey, isLoading } = useQuery<Journey>({
    queryKey: ['journey', id],
    queryFn: () => api.get(`/v1/admin/journeys/${id}`),
    enabled: !!id,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<JourneyFormData>({
    resolver: zodResolver(journeySchema),
    values: journey
      ? {
          title: journey.title,
          slug: journey.slug,
          subtitle: journey.subtitle || '',
          description: journey.description || '',
          duration: journey.duration || '',
          price_from: journey.price_from || undefined,
          price_display: journey.price_display || '',
          status: journey.status,
          featured: journey.featured,
          meta_title: journey.meta_title || '',
          meta_description: journey.meta_description || '',
        }
      : {
          title: '',
          status: 'draft',
          featured: false,
        },
  });

  const saveMutation = useMutation({
    mutationFn: (data: JourneyFormData) =>
      isNew
        ? api.post('/v1/admin/journeys', data)
        : api.put(`/v1/admin/journeys/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journeys'] });
      toast.success(isNew ? 'Journey created' : 'Journey updated');
      navigate('/journeys');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save');
    },
  });

  if (!isNew && isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">
        {isNew ? 'New Journey' : 'Edit Journey'}
      </h1>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              {...register('title')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Subtitle</label>
            <input
              type="text"
              {...register('subtitle')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Duration</label>
              <input
                type="text"
                {...register('duration')}
                placeholder="e.g., 10 days"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Price Display</label>
              <input
                type="text"
                {...register('price_display')}
                placeholder="e.g., From £8,500 per person"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              {...register('description')}
              rows={6}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                {...register('status')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                {...register('featured')}
                className="h-4 w-4 text-brand-bronze focus:ring-brand-bronze border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">Featured on homepage</label>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-medium">SEO</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700">Meta Title</label>
            <input
              type="text"
              {...register('meta_title')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Meta Description</label>
            <textarea
              {...register('meta_description')}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-bronze focus:ring-brand-bronze"
            />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/journeys')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-brand-bronze text-white rounded-md hover:bg-brand-bronze/90 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
